<template>
  <Modal>
    <Modal.Title>How does this look?</Modal.Title>
    <Modal.Body>
      <div
        class="flex h-[180px] w-full items-center justify-center rounded-[8px] border border-gray-800/20"
      >
        <div
          class="size-[130px] rounded-full bg-cover bg-center"
          :style="styles"
        />
      </div>
    </Modal.Body>
    <Modal.Footer>
      <Button variant="stroke" :disabled="loading" @click="handleChangePhoto">
        Change photo
      </Button>
      <Button variant="primary" :loading="loading" @click="handleSubmit">
        Upload
      </Button>
    </Modal.Footer>
  </Modal>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref } from 'vue'
import gql from 'graphql-tag'
import axios from 'axios'
import { Modal, Button, useModal } from '@dacr/library'
import {
  useCreateAvatarUploadUrlMutation,
  type User,
} from '@/shared/graphql/legacy/generated'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import { apolloClient } from '@/shared/graphql/client'
import { useUserStore } from '@/shared/stores/user'
import { NativeActionsEnum, useWebviewStore } from '@/shared/stores/webview'

const { close } = useModal()

const props = defineProps<{
  file: File
}>()

const { mutate: createAvatarUploadUrl } = useCreateAvatarUploadUrlMutation()

const toastStore = useToastStore()
const userStore = useUserStore()
const webviewStore = useWebviewStore()

const imageFile = ref(props.file)
const imageDataUrl = ref('')
const loading = ref(false)

const styles = computed(() => ({
  backgroundImage: `url("${imageDataUrl.value}")`,
}))

const processFile = () => {
  const fileReader = new FileReader()
  fileReader.onload = () => {
    imageDataUrl.value = fileReader.result as string
  }
  fileReader.readAsDataURL(imageFile.value)
}

const handleChangePhoto = () => {
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = 'image/*'
  input.click()

  input.onchange = (event: Event) => {
    const file = (event.target as HTMLInputElement).files?.[0]
    if (!file) return
    imageFile.value = file
    processFile()
  }
}

const handleSubmit = async () => {
  loading.value = true

  const response = await createAvatarUploadUrl({
    fileName: imageFile.value.name,
  })

  const uploadData = response?.data?.createAvatarUploadUrl

  if (!uploadData) {
    loading.value = false
    return
  }

  const { __typename, ...headers } = uploadData.headers

  const formData = new FormData()

  Object.entries(headers).forEach(([key, value]) => {
    if (!value) return
    formData.append(key.replace(/_/g, '-'), value)
  })

  formData.append('file', imageFile.value)

  await axios.post(uploadData.url, formData)

  if (userStore.user) {
    apolloClient.cache.updateFragment<User>(
      {
        id: apolloClient.cache.identify(userStore.user),
        fragment: gql`
          fragment UpdateProfilePicture on User {
            avatarUrl
          }
        `,
      },
      (response) => {
        if (!response || !userStore.user?.avatarUrl) return

        let avatarUrl = ''

        if (userStore.user.avatarUrl.includes('default')) {
          let avatarUrlParts = userStore.user.avatarUrl.split('/').slice(0, -2)
          avatarUrlParts = avatarUrlParts.concat([
            userStore.user.id,
            imageFile.value.name,
          ])

          avatarUrl = avatarUrlParts.join('/')
        } else {
          const avatarUrlParts = userStore.user.avatarUrl.split('/')
          avatarUrlParts[avatarUrlParts.length - 1] = imageFile.value.name

          avatarUrl = avatarUrlParts.join('/')
        }

        return { avatarUrl } as User
      }
    )
  }

  if (webviewStore.isActive) {
    webviewStore.postMessage({
      type: NativeActionsEnum.UserUpdated,
      data: { user: userStore.user },
    })
  }

  toastStore.showToast({
    type: ToastType.Success,
    content: 'Profile picture updated',
  })

  close()

  loading.value = false
}

onBeforeMount(processFile)
</script>
